import { UnitInput, type UnitInputProps } from "$components/inputs";
import React from "react";
import { useController, useFormContext } from "react-hook-form";

type UnitValue = { value?: string; unit?: string };

export const wrapUnitInput =
  <T extends UnitInputProps>(Component: React.ComponentType<T>) =>
  (props: T): React.ReactElement => {
    const { name } = props;
    const { control, setValue, getValues } = useFormContext();
    const valueName = `${name}.value`;
    const defaultValue: UnitValue = getValues(name) ?? { value: "", unit: "" };
    const valueController = useController({
      name: valueName,
      control,
      defaultValue: defaultValue.value,
    });
    const unitController = useController({
      name: `${name}.unit`,
      control,
      defaultValue: defaultValue.unit,
    });

    const {
      field: valueField,
      fieldState: { isTouched: valueIsTouched, error: valueError },
    } = valueController;
    const { value: valueValue, onBlur: handleBlurValue } = valueField;

    const {
      field: unitField,
      fieldState: { isTouched: unitIsTouched, error: unitError },
    } = unitController;
    const {
      value: unitValue,
      onChange: handleChangeUnit,
      onBlur: handleBlurUnit,
    } = unitField;

    const handleChangeValue = React.useCallback(
      (e) => {
        const eventTargetValue = e.target.value;
        // Coerce blank strings to `undefined`,
        // so they are not parsed to `NaN` when casting/validating
        setValue(valueName, eventTargetValue || undefined, {
          shouldValidate: true,
        });
      },
      [valueName, setValue],
    );

    const touched = unitIsTouched || valueIsTouched;
    const error = touched
      ? (unitError?.message ?? valueError?.message)
      : undefined;

    return (
      <Component
        value={{ unit: unitValue, value: valueValue }}
        error={error}
        onChangeUnit={handleChangeUnit}
        onChangeValue={handleChangeValue}
        onBlurUnit={handleBlurUnit}
        onBlurValue={handleBlurValue}
        {...props}
      />
    );
  };

const RHFUnitInput = wrapUnitInput(UnitInput);

export default RHFUnitInput;
